// Restore-key file builder/reader. Optional AES-GCM encryption via passphrase.
// Everything runs locally with the Web Crypto API — the key never leaves the browser.

const enc = new TextEncoder();
const dec = new TextDecoder();

function b64(buf) {
  return btoa(String.fromCharCode(...new Uint8Array(buf)));
}
function unb64(s) {
  return Uint8Array.from(atob(s), (c) => c.charCodeAt(0));
}

async function deriveKey(passphrase, salt) {
  const base = await crypto.subtle.importKey("raw", enc.encode(passphrase), "PBKDF2", false, ["deriveKey"]);
  return crypto.subtle.deriveKey(
    { name: "PBKDF2", salt, iterations: 150000, hash: "SHA-256" },
    base,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"],
  );
}

export async function buildKeyFile(mapping, passphrase) {
  const createdAt = new Date().toISOString();
  if (!passphrase) {
    return JSON.stringify({ v: 1, enc: false, createdAt, mapping }, null, 2);
  }
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const key = await deriveKey(passphrase, salt);
  const payload = enc.encode(JSON.stringify({ v: 1, createdAt, mapping }));
  const ct = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, payload);
  return JSON.stringify({ v: 1, enc: true, createdAt, salt: b64(salt), iv: b64(iv), data: b64(ct) }, null, 2);
}

export function isEncryptedKeyFile(text) {
  try { return !!JSON.parse(text).enc; } catch { return false; }
}

export async function readKeyFile(text, passphrase) {
  let obj;
  try { obj = JSON.parse(text); } catch { throw new Error("Not a valid key file (JSON expected)."); }
  if (!obj.enc) {
    if (!Array.isArray(obj.mapping)) throw new Error("Key file has no mapping data.");
    return obj.mapping;
  }
  if (!passphrase) throw new Error("This key file is encrypted. Enter its passphrase.");
  const key = await deriveKey(passphrase, unb64(obj.salt));
  let ptBuf;
  try {
    ptBuf = await crypto.subtle.decrypt({ name: "AES-GCM", iv: unb64(obj.iv) }, key, unb64(obj.data));
  } catch {
    throw new Error("Incorrect passphrase or corrupted key file.");
  }
  const payload = JSON.parse(dec.decode(ptBuf));
  return payload.mapping;
}
